import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  MapPin, Phone, Globe, Clock, Star, BadgeCheck, Share2, Heart,
  Facebook, Instagram, Twitter, Youtube, ExternalLink, CalendarDays, ChevronRight,
  Camera, Sparkles, CheckCircle2, Navigation, Store, Languages,
} from "lucide-react";
import { toast } from "sonner";
import { api, ACTION_META } from "../lib/api";
import { useLang } from "../lib/i18n";
import ListingCard from "../components/ListingCard";

const SOCIAL_ICONS = { facebook: Facebook, instagram: Instagram, twitter: Twitter, youtube: Youtube };
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const TABS = ["overview", "photos", "reviews", "qna", "nearby"];

function openNow(hours) {
  if (!hours || !Object.keys(hours).length) return null;
  const today = DAYS[new Date().getDay()];
  const expand = (key) => {
    const k = key.trim();
    if (k.includes("-")) {
      const [a, b] = k.split("-").map((s) => s.trim().slice(0, 3));
      const ia = DAYS.indexOf(a);
      const ib = DAYS.indexOf(b);
      if (ia === -1 || ib === -1) return [];
      const out = [];
      let i = ia;
      for (let guard = 0; guard < 8; guard++) {
        out.push(DAYS[i]);
        if (i === ib) break;
        i = (i + 1) % 7;
      }
      return out;
    }
    return [k.slice(0, 3)];
  };
  for (const [key, val] of Object.entries(hours)) {
    if (!expand(key).includes(today)) continue;
    if (/closed/i.test(val)) return { open: false };
    const m = val.match(/(\d+):(\d+)\s*(AM|PM)\s*-\s*(\d+):(\d+)\s*(AM|PM)/i);
    if (!m) return { open: true };
    const toMin = (h, mm, ap) => ((+h % 12) + (ap.toUpperCase() === "PM" ? 12 : 0)) * 60 + +mm;
    const now = new Date().getHours() * 60 + new Date().getMinutes();
    return { open: now >= toMin(m[1], m[2], m[3]) && now <= toMin(m[4], m[5], m[6]) };
  }
  return { open: false };
}

export default function ListingDetail() {
  const { slug } = useParams();
  const { t, tOr } = useLang();
  const [listing, setListing] = useState(null);
  const [missing, setMissing] = useState(false);
  const [countries, setCountries] = useState([]);
  const [nearby, setNearby] = useState([]);
  const [tab, setTab] = useState("overview");
  const [imgIdx, setImgIdx] = useState(0);
  const [saved, setSaved] = useState(false);
  const [review, setReview] = useState({ name: "", rating: 5, text: "" });
  const [reviewSent, setReviewSent] = useState(false);
  const [question, setQuestion] = useState({ name: "", text: "" });
  const [qSent, setQSent] = useState(false);
  const [helpfulVoted, setHelpfulVoted] = useState(() => JSON.parse(localStorage.getItem("bh_helpful") || "[]"));

  useEffect(() => {
    api.get("/countries").then((r) => setCountries(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setListing(null);
    setMissing(false);
    setTab("overview");
    setImgIdx(0);
    api.get(`/listings/${slug}`)
      .then((r) => {
        setListing(r.data);
        api.post(`/listings/${r.data.id}/view`).catch(() => {});
        setSaved((JSON.parse(localStorage.getItem("bh_saved") || "[]")).includes(r.data.id));
        api.get("/listings", { params: { category: r.data.category, limit: 9 } })
          .then((res) => setNearby((res.data || []).filter((x) => x.id !== r.data.id).slice(0, 8)))
          .catch(() => {});
      })
      .catch(() => setMissing(true));
  }, [slug]);

  if (missing) {
    return (
      <div className="py-32 text-center" data-testid="listing-not-found">
        <p className="font-serif italic text-4xl text-gold-dark mb-4">{t("ld.notfound")}</p>
        <Link to="/directory" className="text-ruby font-semibold text-sm underline underline-offset-4" data-testid="back-to-directory-link">
          {t("ld.backtodir")}
        </Link>
      </div>
    );
  }

  if (!listing) {
    return <div className="max-w-7xl mx-auto px-4 py-16"><div className="h-[480px] rounded-2xl bg-sand animate-pulse" /></div>;
  }

  const l = listing;
  const addr = [l.address?.line1, l.address?.city, l.address?.state, l.address?.postal].filter(Boolean).join(", ");
  const countryName = tOr(`country.${l.address?.country}`, countries.find((c) => c.code === l.address?.country)?.name || l.address?.country);
  const actions = Object.entries(l.actions || {}).filter(([, v]) => v);
  const allActions = [...(l.website ? [["website", l.website]] : []), ...actions];
  const images = l.images || [];
  const reviews = l.reviews || [];
  const dist = [5, 4, 3, 2, 1].map((s) => ({ star: s, n: reviews.filter((r) => r.rating === s).length }));
  const openState = openNow(l.hours);
  const track = (type) => api.post(`/listings/${l.id}/track`, { type }).catch(() => {});
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr || l.name)}`;
  const reviewWord = reviews.length !== 1 ? t("ld.review.many") : t("ld.review.one");

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: l.name, url });
      else {
        await navigator.clipboard.writeText(url);
        toast.success(t("toast.copied"));
      }
      track("share");
    } catch {}
  };

  const toggleSave = () => {
    const cur = JSON.parse(localStorage.getItem("bh_saved") || "[]");
    const next = saved ? cur.filter((x) => x !== l.id) : [...cur, l.id];
    localStorage.setItem("bh_saved", JSON.stringify(next));
    if (!saved) track("save");
    setSaved(!saved);
    toast.success(saved ? t("toast.unsaved") : t("toast.saved"));
  };

  const quickActions = [
    l.phone && { icon: Phone, label: t("ld.call"), href: `tel:${l.phone}`, tid: "quick-call", ev: "phone" },
    addr && { icon: Navigation, label: t("ld.directions"), href: mapsUrl, external: true, tid: "quick-directions", ev: "directions" },
    l.website && { icon: Globe, label: t("ld.website"), href: l.website, external: true, tid: "quick-website", ev: "website" },
  ].filter(Boolean);

  const inputCls = "w-full px-4 py-3 rounded-xl bg-sand/60 text-sm outline-none border border-transparent focus:border-gold";

  const markHelpful = async (rid) => {
    const voted = JSON.parse(localStorage.getItem("bh_helpful") || "[]");
    if (voted.includes(rid)) return;
    localStorage.setItem("bh_helpful", JSON.stringify([...voted, rid]));
    setHelpfulVoted((v) => [...v, rid]);
    api.post(`/reviews/${rid}/helpful`).catch(() => {});
    setListing((prev) => prev ? { ...prev, reviews: prev.reviews.map((r) => (r.id === rid ? { ...r, helpful: (r.helpful || 0) + 1 } : r)) } : prev);
  };

  const submitQuestion = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/listings/${l.id}/questions`, question);
      setQSent(true);
      toast.success(t("toast.q.ok"));
    } catch {
      toast.error(t("toast.q.err"));
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/listings/${l.id}/reviews`, review);
      setReviewSent(true);
      toast.success(t("toast.r.ok"));
    } catch {
      toast.error(t("toast.r.err"));
    }
  };

  const tabLabel = { overview: t("ld.tab.overview"), photos: `${t("ld.tab.photos")}${images.length ? ` (${images.length})` : ""}`, reviews: `${t("ld.tab.reviews")}${reviews.length ? ` (${reviews.length})` : ""}`, qna: `${t("ld.tab.qna")}${(l.questions || []).length ? ` (${l.questions.length})` : ""}`, nearby: t("ld.tab.nearby") };

  return (
    <div className="bg-cream min-h-screen" data-testid="listing-detail-page">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-[#E6DED3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
          <nav className="flex items-center gap-1.5 text-xs sm:text-sm text-stone2 overflow-x-auto whitespace-nowrap scrollbar-none" data-testid="breadcrumb">
            <Link to="/" className="hover:text-ruby">{t("ld.home")}</Link>
            {countryName && (<><ChevronRight className="w-3.5 h-3.5 shrink-0" /><Link to={`/search?country=${l.address.country}`} className="hover:text-ruby">{countryName}</Link></>)}
            {l.address?.state && (<><ChevronRight className="w-3.5 h-3.5 shrink-0" /><Link to={`/search?country=${l.address.country}&state=${encodeURIComponent(l.address.state)}`} className="hover:text-ruby">{l.address.state}</Link></>)}
            {l.address?.city && (<><ChevronRight className="w-3.5 h-3.5 shrink-0" /><Link to={`/search?country=${l.address.country}&state=${encodeURIComponent(l.address.state || "")}&city=${encodeURIComponent(l.address.city)}`} className="hover:text-ruby">{l.address.city}</Link></>)}
            <ChevronRight className="w-3.5 h-3.5 shrink-0" />
            <span className="text-ink font-semibold truncate">{l.name}</span>
          </nav>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={share} className="inline-flex items-center gap-1.5 text-xs font-semibold text-clay hover:text-ruby px-3 py-1.5 rounded-full border border-[#E6DED3]" data-testid="share-btn">
              <Share2 className="w-3.5 h-3.5" /> {t("ld.share")}
            </button>
            <button onClick={toggleSave} className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${saved ? "bg-ruby-light text-ruby border-ruby/30" : "text-clay hover:text-ruby border-[#E6DED3]"}`} data-testid="save-btn">
              <Heart className={`w-3.5 h-3.5 ${saved ? "fill-ruby" : ""}`} /> {saved ? t("ld.saved") : t("ld.save")}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        {/* Gallery + Info */}
        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7">
            <div className="relative rounded-2xl overflow-hidden border border-[#E6DED3] bg-sand" data-testid="detail-gallery">
              {l.featured && <span className="absolute top-3 left-3 bg-gold text-ink text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full shadow-md z-10" data-testid="detail-featured-badge">{t("common.featured")}</span>}
              {l.sponsored && <span className="absolute top-3 right-3 bg-ruby text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full shadow-md z-10" data-testid="detail-sponsored-badge">{t("common.sponsored")}</span>}
              {images.length > 0 ? (
                <img src={images[imgIdx]} alt={l.name} className="w-full h-[320px] sm:h-[480px] object-cover" />
              ) : (
                <div className="w-full h-[320px] sm:h-[480px] bg-sand flex items-center justify-center">
                  <span className="font-serif italic text-3xl text-gold-dark/60">{tOr(`cat.${l.category}`, l.category_label)}</span>
                </div>
              )}
              {images.length > 0 && (
                <button onClick={() => setTab("photos")} className="absolute bottom-3 right-3 inline-flex items-center gap-2 bg-ink/80 backdrop-blur text-cream text-xs font-semibold px-3.5 py-2 rounded-lg hover:bg-ink" data-testid="see-all-photos-btn">
                  <Camera className="w-3.5 h-3.5" /> {t("ld.seeallphotos")} ({images.length})
                </button>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex gap-3 mt-3 overflow-x-auto pb-1 scrollbar-none" data-testid="gallery-thumbs">
                {images.map((src, i) => (
                  <button key={i} onClick={() => setImgIdx(i)} data-testid={`gallery-thumb-${i}`}
                    className={`w-24 h-16 shrink-0 rounded-lg overflow-hidden border-2 transition-colors ${i === imgIdx ? "border-gold" : "border-transparent opacity-70 hover:opacity-100"}`}>
                    <img src={src} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-5">
            <div className="bg-white rounded-2xl border border-[#E6DED3] p-6 lg:p-7" data-testid="detail-info-card">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-ruby mb-1.5">{tOr(`cat.${l.category}`, l.category_label)}</p>
              <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-ink flex items-center gap-2" data-testid="detail-name">
                {l.logo && <img src={l.logo} alt={`${l.name} logo`} className="w-11 h-11 rounded-xl border border-[#E2E8F0] object-cover shrink-0" data-testid="detail-logo" />}
                {l.name}
                {l.verified && <BadgeCheck className="w-5 h-5 text-jade shrink-0" data-testid="detail-verified-badge" />}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm">
                {l.rating_avg ? (
                  <>
                    <span className="flex items-center gap-1 font-semibold text-ink" data-testid="detail-rating">
                      <Star className="w-4 h-4 fill-gold text-gold" /> {l.rating_avg}
                    </span>
                    <span className="text-stone2">{reviews.length} {reviewWord}</span>
                    <button onClick={() => setTab("reviews")} className="text-ruby font-semibold text-xs underline underline-offset-2" data-testid="write-review-link">{t("ld.reviews.write")}</button>
                  </>
                ) : (
                  <button onClick={() => setTab("reviews")} className="text-ruby font-semibold text-xs underline underline-offset-2" data-testid="write-review-link">{t("ld.reviews.first")}</button>
                )}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
                {l.price && <span className="font-display font-bold text-ruby" data-testid="detail-price">{l.price}</span>}
                {l.event_date && (
                  <span className="flex items-center gap-1.5 text-jade font-medium">
                    <CalendarDays className="w-4 h-4" />
                    {new Date(l.event_date + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                  </span>
                )}
              </div>

              {quickActions.length > 0 && (
                <div className="mt-5 grid grid-cols-3 gap-2" data-testid="quick-actions">
                  {quickActions.map((a) => (
                    <a key={a.tid} href={a.href} {...(a.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      onClick={() => track(a.ev)}
                      data-testid={a.tid}
                      className="flex flex-col items-center gap-1.5 py-3 rounded-xl bg-sand/60 hover:bg-gold-light text-clay hover:text-ink transition-colors">
                      <a.icon className="w-5 h-5" />
                      <span className="text-[11px] font-semibold">{a.label}</span>
                    </a>
                  ))}
                </div>
              )}

              <div className="mt-5 pt-5 border-t border-sand space-y-3.5 text-sm">
                {addr && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-gold-dark shrink-0 mt-0.5" />
                    <div>
                      <p className="text-clay leading-relaxed">{addr}{countryName ? `, ${countryName}` : ""}</p>
                      <button onClick={() => document.getElementById("listing-map")?.scrollIntoView({ behavior: "smooth" })} className="text-ruby text-xs font-semibold mt-0.5" data-testid="view-on-map-link">{t("ld.viewmap")}</button>
                    </div>
                  </div>
                )}
                {l.phone && (
                  <p className="flex items-center gap-3 text-clay">
                    <Phone className="w-4 h-4 text-gold-dark shrink-0" />
                    <a href={`tel:${l.phone}`} className="hover:text-ruby" data-testid="detail-phone">{l.phone}</a>
                  </p>
                )}
                {l.website && (
                  <p className="flex items-center gap-3 text-clay">
                    <Globe className="w-4 h-4 text-gold-dark shrink-0" />
                    <a href={l.website} target="_blank" rel="noopener noreferrer" className="hover:text-ruby truncate" data-testid="detail-website">{l.website.replace(/^https?:\/\//, "")}</a>
                  </p>
                )}
                {Object.keys(l.socials || {}).filter((k) => l.socials[k]).length > 0 && (
                  <div className="flex items-center gap-3 pt-1" data-testid="detail-socials">
                    <span className="text-xs text-stone2">{t("ld.followus")}</span>
                    {Object.entries(l.socials).filter(([, v]) => v).map(([k, v]) => {
                      const Icon = SOCIAL_ICONS[k] || Globe;
                      return (
                        <a key={k} href={v} target="_blank" rel="noopener noreferrer" data-testid={`social-${k}`}
                          className="w-8 h-8 rounded-full bg-sand flex items-center justify-center text-clay hover:bg-ruby hover:text-cream transition-colors">
                          <Icon className="w-3.5 h-3.5" />
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-8 border-b border-[#E6DED3] flex gap-1 sm:gap-2 overflow-x-auto scrollbar-none" data-testid="detail-tabs">
          {TABS.map((tabKey) => (
            <button key={tabKey} onClick={() => setTab(tabKey)} data-testid={`tab-${tabKey}`}
              className={`px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === tabKey ? "border-ruby text-ruby" : "border-transparent text-stone2 hover:text-ink"}`}>
              {tabLabel[tabKey]}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-12 gap-6 mt-6">
          {/* Main column */}
          <div className="lg:col-span-8 space-y-8">
            {tab === "overview" && (
              <>
                <div data-testid="detail-description">
                  <h2 className="font-display text-xl font-bold text-ink mb-3">{t("ld.about")}</h2>
                  <p className="text-clay leading-relaxed whitespace-pre-line text-sm sm:text-base">{l.description}</p>
                </div>

                {(Object.keys(l.details || {}).length > 0 || (l.languages || []).length > 0) && (
                  <div className="bg-white rounded-2xl border border-[#E6DED3] p-6" data-testid="detail-attributes">
                    <div className="space-y-3">
                      {Object.entries(l.details || {}).map(([k, v]) => (
                        <div key={k} className="grid grid-cols-[140px_1fr] gap-3 text-sm">
                          <span className="text-stone2">{k}</span>
                          <span className="text-ink font-medium">{v}</span>
                        </div>
                      ))}
                      {(l.languages || []).length > 0 && (
                        <div className="grid grid-cols-[140px_1fr] gap-3 text-sm" data-testid="detail-languages">
                          <span className="text-stone2 flex items-center gap-1.5"><Languages className="w-3.5 h-3.5" />{t("ld.languages")}</span>
                          <span className="text-ink font-medium">{l.languages.join(", ")}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {(l.highlights || []).length > 0 && (
                  <div className="bg-white rounded-2xl border border-[#E6DED3] p-6" data-testid="detail-highlights">
                    <h3 className="font-display text-lg font-bold text-ink mb-4">{t("ld.highlights")}</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {l.highlights.map((h) => (
                        <div key={h} className="flex flex-col items-center text-center gap-2 p-3 rounded-xl bg-gold-light/60 border border-gold/25">
                          <Sparkles className="w-5 h-5 text-gold-dark" />
                          <span className="text-xs font-semibold text-ink leading-tight">{h}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(l.facilities || []).length > 0 && (
                  <div className="bg-white rounded-2xl border border-[#E6DED3] p-6" data-testid="detail-facilities">
                    <h3 className="font-display text-lg font-bold text-ink mb-4">{t("ld.facilities")}</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                      {l.facilities.map((f) => (
                        <p key={f} className="flex items-center gap-2 text-sm text-clay">
                          <CheckCircle2 className="w-4 h-4 text-jade shrink-0" /> {f}
                        </p>
                      ))}
                    </div>
                  </div>
                )}

                {(l.service_options || []).length > 0 && (
                  <div className="bg-white rounded-2xl border border-[#E6DED3] p-6" data-testid="detail-service-options">
                    <h3 className="font-display text-lg font-bold text-ink mb-4">{t("ld.serviceopts")}</h3>
                    <div className="flex flex-wrap gap-2">
                      {l.service_options.map((s) => (
                        <span key={s} className="inline-flex items-center gap-1.5 text-xs font-semibold text-jade bg-jade-light px-3 py-1.5 rounded-full">
                          <CheckCircle2 className="w-3.5 h-3.5" /> {s}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {tab === "photos" && (
              <div data-testid="photos-tab">
                {images.length === 0 ? (
                  <p className="text-sm text-stone2 py-10 text-center">{t("ld.nophotos")}</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {images.map((src, i) => (
                      <button key={i} onClick={() => { setImgIdx(i); setTab("overview"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                        className="rounded-xl overflow-hidden border border-[#E6DED3] hover:border-gold transition-colors" data-testid={`photo-tile-${i}`}>
                        <img src={src} alt="" className="w-full h-40 object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {tab === "reviews" && (
              <div data-testid="reviews-section">
                <h2 className="font-display text-xl font-bold text-ink mb-5">{t("ld.reviews.t")}</h2>
                {reviews.length === 0 ? (
                  <p className="text-sm text-stone2 mb-8">{t("ld.reviews.none")}</p>
                ) : (
                  <div className="space-y-4 mb-10">
                    {reviews.map((r) => (
                      <div key={r.id} className="bg-white border border-[#E6DED3] rounded-xl p-5" data-testid={`review-${r.id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-semibold text-ink text-sm">{r.name} <span className="font-normal text-xs text-stone2">· {(r.created_at || "").slice(0, 10)}</span></p>
                          <span className="flex gap-0.5">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? "fill-gold text-gold" : "text-sand"}`} />
                            ))}
                          </span>
                        </div>
                        <p className="text-sm text-clay leading-relaxed">{r.text}</p>
                        {r.reply && (
                          <div className="mt-3 pl-4 border-l-2 border-gold" data-testid={`review-reply-${r.id}`}>
                            <p className="text-sm text-clay leading-relaxed">{r.reply}</p>
                            <p className="text-[10px] uppercase tracking-wider text-jade font-semibold mt-1.5">{t("ld.owner.reply")}</p>
                          </div>
                        )}
                        <button
                          onClick={() => markHelpful(r.id)}
                          disabled={helpfulVoted.includes(r.id)}
                          className="mt-2.5 text-xs font-semibold text-stone2 hover:text-ruby disabled:text-jade transition-colors"
                          data-testid={`helpful-${r.id}`}
                        >
                          {helpfulVoted.includes(r.id) ? t("ld.helpful.marked") : t("ld.helpful")}{(r.helpful || 0) > 0 ? ` (${(r.helpful || 0)})` : ""}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {reviewSent ? (
                  <p className="bg-jade-light border border-jade/30 text-jade text-sm rounded-xl px-5 py-4" data-testid="review-success">
                    {t("ld.review.success")}
                  </p>
                ) : (
                  <form onSubmit={submitReview} className="bg-white border border-[#E6DED3] rounded-2xl p-6 space-y-4" data-testid="review-form">
                    <p className="font-display font-bold text-ink">{t("ld.reviews.write")}</p>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <input required value={review.name} onChange={(e) => setReview({ ...review, name: e.target.value })} placeholder={t("ld.review.name")} className={inputCls} data-testid="review-name-input" />
                      <select value={review.rating} onChange={(e) => setReview({ ...review, rating: Number(e.target.value) })} className={inputCls} data-testid="review-rating-select">
                        {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} {n > 1 ? t("ld.stars") : t("ld.star")}</option>)}
                      </select>
                    </div>
                    <textarea required value={review.text} onChange={(e) => setReview({ ...review, text: e.target.value })} placeholder={t("ld.review.ph")} rows={3} className={inputCls} data-testid="review-text-input" />
                    <button type="submit" className="bg-ruby hover:bg-ruby-dark text-cream text-sm font-semibold px-6 py-3 rounded-full transition-colors" data-testid="review-submit-btn">
                      {t("ld.review.submit")}
                    </button>
                  </form>
                )}
              </div>
            )}

            {tab === "qna" && (
              <div data-testid="qna-tab">
                <h2 className="font-display text-xl font-bold text-ink mb-5">{t("ld.qna.t")}</h2>
                {(l.questions || []).length === 0 ? (
                  <p className="text-sm text-stone2 mb-8">{t("ld.qna.none")}</p>
                ) : (
                  <div className="space-y-4 mb-10">
                    {(l.questions || []).map((q) => (
                      <div key={q.id} className="bg-white border border-[#E6DED3] rounded-xl p-5" data-testid={`question-${q.id}`}>
                        <p className="text-sm font-semibold text-ink">{q.text}</p>
                        <p className="text-xs text-stone2 mt-1">{t("ld.qna.askedby")} {q.name}</p>
                        {q.answer ? (
                          <div className="mt-3 pl-4 border-l-2 border-gold">
                            <p className="text-sm text-clay leading-relaxed">{q.answer}</p>
                            <p className="text-[10px] uppercase tracking-wider text-jade font-semibold mt-1.5">{t("ld.qna.owneranswer")}</p>
                          </div>
                        ) : (
                          <p className="text-xs text-stone2 italic mt-2">{t("ld.qna.notyet")}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {qSent ? (
                  <p className="bg-jade-light border border-jade/30 text-jade text-sm rounded-xl px-5 py-4" data-testid="question-success">
                    {t("ld.qna.success")}
                  </p>
                ) : (
                  <form onSubmit={submitQuestion} className="bg-white border border-[#E6DED3] rounded-2xl p-6 space-y-4" data-testid="question-form">
                    <p className="font-display font-bold text-ink">{t("ld.qna.ask")}</p>
                    <input required value={question.name} onChange={(e) => setQuestion({ ...question, name: e.target.value })} placeholder={t("ld.review.name")} className={inputCls} data-testid="question-name-input" />
                    <textarea required value={question.text} onChange={(e) => setQuestion({ ...question, text: e.target.value })} placeholder={t("ld.qna.ph")} rows={2} className={inputCls} data-testid="question-text-input" />
                    <button type="submit" className="bg-ink hover:bg-ruby text-cream text-sm font-semibold px-6 py-3 rounded-full transition-colors" data-testid="question-submit-btn">
                      {t("ld.qna.submit")}
                    </button>
                  </form>
                )}
              </div>
            )}

            {tab === "nearby" && (
              <div data-testid="nearby-tab">
                {nearby.length === 0 ? (
                  <p className="text-sm text-stone2 py-10 text-center">{t("ld.nearby.none")}</p>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-5">
                    {nearby.map((n) => <ListingCard key={n.id} listing={n} />)}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4 space-y-6">
            {Object.keys(l.hours || {}).length > 0 && (
              <div className="bg-white rounded-2xl border border-[#E6DED3] p-6" data-testid="detail-hours">
                <h3 className="font-display text-lg font-bold text-ink mb-4 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gold-dark" /> {t("ld.hours")}
                </h3>
                <div className="space-y-2">
                  {Object.entries(l.hours).map(([day, h]) => (
                    <p key={day} className="flex justify-between gap-4 text-sm">
                      <span className="text-stone2">{day}</span>
                      <span className="text-ink font-medium text-right">{h}</span>
                    </p>
                  ))}
                </div>
                {openState && (
                  <p className={`mt-4 pt-3 border-t border-sand text-xs font-bold uppercase tracking-wider ${openState.open ? "text-jade" : "text-ruby"}`} data-testid="open-now-badge">
                    {openState.open ? t("ld.opennow") : t("ld.closednow")}
                  </p>
                )}
              </div>
            )}

            {reviews.length > 0 && (
              <div className="bg-white rounded-2xl border border-[#E6DED3] p-6" data-testid="rating-summary">
                <h3 className="font-display text-lg font-bold text-ink mb-4">{t("ld.ratingsummary")}</h3>
                <div className="flex items-center gap-4 mb-4">
                  <p className="font-display text-4xl font-extrabold text-ink">{l.rating_avg}</p>
                  <div>
                    <span className="flex gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`w-4 h-4 ${i < Math.round(l.rating_avg) ? "fill-gold text-gold" : "text-sand"}`} />
                      ))}
                    </span>
                    <p className="text-xs text-stone2 mt-1">{reviews.length} {reviewWord}</p>
                  </div>
                </div>
                <div className="space-y-1.5">
                  {dist.map((d) => (
                    <div key={d.star} className="flex items-center gap-2 text-xs">
                      <span className="w-3 text-stone2">{d.star}</span>
                      <Star className="w-3 h-3 fill-gold text-gold" />
                      <div className="flex-1 h-2 rounded-full bg-sand overflow-hidden">
                        <div className="h-full bg-gold rounded-full" style={{ width: `${reviews.length ? (d.n / reviews.length) * 100 : 0}%` }} />
                      </div>
                      <span className="w-8 text-right text-stone2">{reviews.length ? Math.round((d.n / reviews.length) * 100) : 0}%</span>
                    </div>
                  ))}
                </div>
                <button onClick={() => setTab("reviews")} className="mt-4 text-xs font-semibold text-ruby" data-testid="see-all-reviews-link">{t("ld.seeallreviews")}</button>
              </div>
            )}

            {allActions.length > 0 && (
              <div className="bg-white border border-[#E6DED3] rounded-2xl p-6" data-testid="detail-actions-card">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone2 mb-4">{t("ld.takeaction")}</p>
                <div className="space-y-2.5">
                  {allActions.map(([key, url], i) => (
                    <a key={key} href={url} target="_blank" rel="noopener noreferrer" onClick={() => key === "website" && track("website")} data-testid={`action-btn-${key}`}
                      className={`flex items-center justify-between w-full px-5 py-3.5 rounded-xl font-semibold text-sm transition-colors duration-200 ${
                        i === 0 ? "bg-ruby text-cream hover:bg-ruby-dark" : "bg-sand/70 text-ink hover:bg-gold-light hover:text-ruby"
                      }`}>
                      {tOr(`act.${key}`, ACTION_META[key]?.label || key)}
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {addr && (
              <div className="bg-white rounded-2xl border border-[#E6DED3] overflow-hidden" id="listing-map" data-testid="detail-map">
                <p className="px-6 pt-5 pb-3 font-display text-lg font-bold text-ink">{t("ld.location")}</p>
                <iframe title="Map" src={`https://www.google.com/maps?q=${encodeURIComponent(addr)}&output=embed`} className="w-full h-56 border-0" loading="lazy" />
                <div className="p-4">
                  <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full bg-ink hover:bg-ruby text-cream text-sm font-semibold py-3 rounded-xl transition-colors" data-testid="get-directions-btn">
                    <Navigation className="w-4 h-4" /> {t("ld.getdirections")}
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* You may also like */}
        {nearby.length > 0 && tab !== "nearby" && (
          <section className="mt-12" data-testid="you-may-also-like">
            <div className="flex items-end justify-between mb-6">
              <h2 className="font-display text-xl sm:text-2xl font-bold text-ink">{t("ld.alsolike")}</h2>
              <button onClick={() => setTab("nearby")} className="text-xs font-semibold text-ruby" data-testid="see-nearby-link">{t("ld.seenearby")}</button>
            </div>
            <div className="flex gap-5 overflow-x-auto pb-4 snap-x scrollbar-none">
              {nearby.map((n) => (
                <div key={n.id} className="min-w-[280px] sm:min-w-[300px] snap-start">
                  <ListingCard listing={n} />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Claim banner */}
        <section className="mt-12 bg-white border border-[#E6DED3] rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5" data-testid="claim-banner">
          <span className="w-12 h-12 rounded-xl bg-gold-light border border-gold/40 flex items-center justify-center text-gold-dark shrink-0">
            <Store className="w-6 h-6" />
          </span>
          <div className="flex-1">
            <p className="font-display font-bold text-ink text-lg">{t("ld.claim.t")}</p>
            <p className="text-sm text-clay mt-1">{t("ld.claim.s")}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/owner/login" className="bg-ruby hover:bg-ruby-dark text-cream text-sm font-semibold px-6 py-3 rounded-full transition-colors" data-testid="claim-business-btn">
              {t("ld.claim.btn")}
            </Link>
            <a href="mailto:admin@burmesehub.com" className="border border-[#E6DED3] hover:border-gold text-ink text-sm font-semibold px-6 py-3 rounded-full transition-colors" data-testid="suggest-update-btn">
              {t("ld.suggest")}
            </a>
          </div>
        </section>
      </div>
    </div>
  );
}
